import {ActionContext, ActionTree} from 'vuex';
import {RootState} from '@/store';
import {ProfileState, SYNC_ERROR_MSG} from '@/store/modules/Profile/state';
import router from '@/plugins/router';
import {_axios} from '@/plugins/axios';
import {AdvertAuthor, BlackListUser, MyProfileAPI, ProfilDetailAPI} from '#/User';
import {AxiosError, AxiosResponse} from 'axios';

export const actions: ActionTree<ProfileState, RootState> = {
    /**
     * À la première connexion, l'utilisateur accepte les conditions d'utilisation et crée son profil.
     */
    createProfile: (
        {commit, dispatch}: ActionContext<ProfileState, RootState>,
        profile: MyProfileAPI,
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.post('profile/', profile)
                  .then((response: AxiosResponse<MyProfileAPI>) => {
                      const {syncError, ...profile} = response.data;
                      commit('GET_PROFILE', profile);

                      if (syncError) {
                          dispatch(
                              'displaySnackBar',
                              {text: SYNC_ERROR_MSG, type: 'error'},
                              {root: true},
                          )
                              .then(() => {
                                  resolve();
                              });

                      }

                      router.push({name: 'ProfileDetail'})
                            .then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                  })
                  .catch((error: AxiosError) => {
                      if (error.response?.data.code === 'user_not_student') {
                          router.push({name: 'NoStudent'})
                                .then(() => {
                                    resolve();
                                })
                                .catch((error) => {
                                    reject();
                                });
                      } else {
                          dispatch('displayError', error.response, {root: true})
                              .then(() => {
                                  resolve();
                              });
                      }
                  });
        });
    },

    loadAuthorProfile: (
        {commit, dispatch, state}: ActionContext<ProfileState, RootState>,
        {id}: AdvertAuthor,
    ): Promise<void> => {
        return new Promise(resolve => {
            _axios.get(`profile/${id}/`)
                  .then((response: AxiosResponse<ProfilDetailAPI>) => {
                      const {lastLogin, ...profilInfos} = response.data;
                      const censured = state.profile.blacklist.some(bl => bl.id === id);
                      commit('GET_AUTHOR_PROFILE', {
                          lastLogin: lastLogin ? new Date(lastLogin) : undefined,
                          censured,
                          ...profilInfos,
                      });
                      resolve();
                  })
                  .catch(error => {
                      router.go(-1);
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * Sur la page d'accueil, on charge les informations sur l'utilisateur.
     */
    loadProfile: (
        {commit, state}: ActionContext<ProfileState, RootState>,
    ): Promise<void | MyProfileAPI> => {
        return new Promise((resolve, reject) => {
            if (!state.profileStored) {
                _axios.get('profile/')
                      .then((response: AxiosResponse<MyProfileAPI>) => {
                          commit('GET_PROFILE', response.data);
                          resolve(response.data);
                      })
                      .catch((error: AxiosError) => {
                          reject(error);
                      });
            } else {
                resolve();
            }
        });
    },

    sync: (
        {commit, dispatch}: ActionContext<ProfileState, RootState>,
        {username, useErnestProfile}: MyProfileAPI,
    ): Promise<void> => {
        return new Promise(resolve => {
            _axios.patch('profile/sync/', {username, useErnestProfile})
                  .then(response => {
                      commit('GET_PROFILE', response.data);
                      resolve();
                  })
                  .catch(() => {
                      dispatch(
                          'displaySnackBar',
                          {text: SYNC_ERROR_MSG, type: 'error'},
                          {root: true},
                      )
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    updateProfile: (
        {commit, dispatch, state}: ActionContext<ProfileState, RootState>,
        user: MyProfileAPI,
    ): Promise<MyProfileAPI> => {
        return new Promise((resolve, reject) => {
            state.profileStored = false;

            _axios.patch('profile/', user)
                  .then((response: AxiosResponse<MyProfileAPI>) => {
                      commit('GET_PROFILE', response.data);
                      resolve(response.data);
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              reject();
                          });
                  });
        });
    },

    blacklistToggle: (
        {commit}: ActionContext<ProfileState, RootState>,
        {blacklist, user}: { blacklist: boolean, user: ProfilDetailAPI },
    ): Promise<void> => {
        return new Promise(resolve => {
            _axios.put(
                'profile/blacklist/',
                {action: blacklist ? 'blacklist' : 'whitelist', userId: user.id},
            )
                  .then(() => {
                      commit('UPDATE_BLACKLIST', user);
                      resolve();
                  });
        });
    },

    loadBlacklist: (
        {commit}: ActionContext<ProfileState, RootState>,
    ): Promise<void> => {
        return new Promise(resolve => {
            _axios.get('profile/blacklist/')
                  .then((response: AxiosResponse<BlackListUser[]>) => {
                      commit('LOAD_BLACKLIST', response.data);
                      resolve();
                  });
        });
    },
};
